import { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import {
  LEAD_EVENT_TYPES,
  RENTALS_TYPES,
  type LeadEventType,
  type RentalsType,
  type ProjectWithConfig,
  type RentalsTypeWithConfig,
} from "../shared/types";
import { getEventTypeLabel, getEventTypeDescription } from "../shared/utils";
import { VariableHelpTooltip } from "../shared/VariableHelpTooltip";
import {
  PIPEDRIVE_CONDITIONS,
  type PipedriveAction,
  type PipedriveCondition,
  type PipedriveEventMapping,
  getPipedriveActionLabel,
  getPipedriveConditionLabel,
  getAvailableActionsForEvent,
} from "./types";

const PipedriveCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);
  const [selectedPipelines, setSelectedPipelines] = useState<Record<string, number>>({});
  const [pipelines, setPipelines] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [eventMappings, setEventMappings] = useState<Record<string, PipedriveEventMapping[]>>({});
  const [stages, setStages] = useState<Record<string, Array<{ id: number; name: string }>>>({});
  const [loadingStages, setLoadingStages] = useState<Record<string, boolean>>({});
  const [activityTypes, setActivityTypes] = useState<
    Array<{ id: number; name: string; key_string: string }>
  >([]);
  const [loadingActivityTypes, setLoadingActivityTypes] = useState(false);

  const { data: projects, isLoading: isLoadingProjects } = trpc.crmSelfConfig.listProjects.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId && merchantType === "development" }
  );

  const { data: crmConfigurations, isLoading: isLoadingConfigurations } =
    trpc.crmSelfConfig.listCrmConfigurations.useQuery(
      { merchantId: merchantId || "" },
      { enabled: !!merchantId }
    );

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const projectsWithConfig = useMemo(() => {
    if (merchantType !== "development" || !projects || !crmConfigurations) return [];

    return projects.map((project) => {
      const config = crmConfigurations.find((c) => c.projectId === project.id);
      return {
        id: project.id,
        name: project.name,
        crmConfiguration: config
          ? {
              id: config.id,
              pipelineId: config.pipedrive?.pipelineId,
              pipelineName: config.pipedrive?.pipelineName,
              pipedriveEventsMapping: (config.pipedriveEventsMapping ||
                []) as PipedriveEventMapping[],
            }
          : undefined,
      };
    });
  }, [projects, crmConfigurations, merchantType]);

  const rentalsTypesWithConfig = useMemo(() => {
    if (merchantType !== "rentals" || !crmConfigurations) return [];

    return RENTALS_TYPES.map((rentalsType) => {
      const config = crmConfigurations.find((c) => c.rentalsType === rentalsType.id);
      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config
          ? {
              id: config.id,
              pipelineId: config.pipedrive?.pipelineId,
              pipelineName: config.pipedrive?.pipelineName,
              pipedriveEventsMapping: (config.pipedriveEventsMapping ||
                []) as PipedriveEventMapping[],
            }
          : undefined,
      };
    });
  }, [crmConfigurations, merchantType]);

  useEffect(() => {
    if (merchantType === "development" && projectsWithConfig.length > 0) {
      const initialPipelines: Record<string, number> = {};
      const initialMappings: Record<string, PipedriveEventMapping[]> = {};
      projectsWithConfig.forEach((project) => {
        if (project.crmConfiguration?.pipelineId) {
          initialPipelines[project.id] = project.crmConfiguration.pipelineId as number;
        }
        if (project.crmConfiguration?.pipedriveEventsMapping) {
          initialMappings[project.id] = project.crmConfiguration.pipedriveEventsMapping;
        }
      });
      setSelectedPipelines(initialPipelines);
      setEventMappings(initialMappings);
    } else if (merchantType === "rentals" && rentalsTypesWithConfig.length > 0) {
      const initialPipelines: Record<string, number> = {};
      const initialMappings: Record<string, PipedriveEventMapping[]> = {};
      rentalsTypesWithConfig.forEach((rentalsType) => {
        if (rentalsType.crmConfiguration?.pipelineId) {
          initialPipelines[rentalsType.id] = rentalsType.crmConfiguration.pipelineId as number;
        }
        if (rentalsType.crmConfiguration?.pipedriveEventsMapping) {
          initialMappings[rentalsType.id] = rentalsType.crmConfiguration.pipedriveEventsMapping;
        }
      });
      setSelectedPipelines(initialPipelines);
      setEventMappings(initialMappings);
    }
  }, [projectsWithConfig, rentalsTypesWithConfig, merchantType]);

  const utils = trpc.useUtils();
  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración creada", {
        description: "La configuración del proyecto se ha creado correctamente.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la configuración.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración actualizada", {
        description: "La configuración del proyecto se ha actualizado correctamente.",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al actualizar la configuración.",
      });
    },
  });

  const getPipelines = trpc.crmSelfConfig.getPipelines.useMutation({
    onSuccess: (result) => {
      if (result.success && result.pipelines) {
        setPipelines(
          result.pipelines.map((p) => ({
            id: typeof p.id === "number" ? p.id : parseInt(p.id as string),
            name: p.name,
          }))
        );
        setLoadingPipelines(false);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los pipelines.",
        });
        setLoadingPipelines(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los pipelines.",
      });
      setLoadingPipelines(false);
    },
  });

  const getStages = trpc.crmSelfConfig.getStages.useMutation();
  const getActivityTypes = trpc.crmSelfConfig.getActivityTypes.useMutation();

  const handleLoadStages = useCallback(
    async (id: string, pipelineId: number) => {
      if (!crmConnection?.provider || !crmConnection.pipedrive?.apiKey) {
        toast.error("Error", {
          description: "No hay conexión CRM configurada.",
        });
        return;
      }

      if (loadingStages[id] || stages[id]) {
        return;
      }

      setLoadingStages((prev) => ({ ...prev, [id]: true }));

      try {
        const result = await getStages.mutateAsync({
          provider: crmConnection.provider,
          apiKey: crmConnection.pipedrive.apiKey,
          pipelineId,
        });

        if (result.success && result.stages) {
          setStages((prev) => ({
            ...prev,
            [id]:
              result.stages?.map((s) => ({
                id: typeof s.id === "number" ? s.id : parseInt(s.id as string),
                name: s.name,
              })) || [],
          }));
        } else {
          toast.error("Error", {
            description: result.error || "No se pudieron obtener las etapas.",
          });
        }
      } catch (error: any) {
        toast.error("Error", {
          description: error.message || "Ocurrió un error al obtener las etapas.",
        });
      } finally {
        setLoadingStages((prev) => ({ ...prev, [id]: false }));
      }
    },
    [crmConnection, getStages, loadingStages, stages]
  );

  const handleLoadPipelines = async () => {
    if (!crmConnection?.provider || !crmConnection.pipedrive?.apiKey) {
      toast.error("Error", {
        description: "No hay conexión CRM configurada.",
      });
      return;
    }

    setLoadingPipelines(true);
    try {
      await getPipelines.mutateAsync({
        provider: crmConnection.provider,
        apiKey: crmConnection.pipedrive.apiKey,
      });
    } catch (error) {
      // Error is already handled in onError callback
    }
  };

  const handleLoadActivityTypes = useCallback(async () => {
    if (!crmConnection?.provider || !crmConnection.pipedrive?.apiKey) {
      return;
    }

    // This component is only for Pipedrive, so we can safely assert the provider type
    if (crmConnection.provider !== "pipedrive") {
      return;
    }

    setLoadingActivityTypes(true);

    try {
      const result = await getActivityTypes.mutateAsync({
        provider: crmConnection.provider as "pipedrive" | "zoho" | "bitrix" | "custom",
        apiKey: crmConnection.pipedrive.apiKey,
      });

      if (result.success && result.activityTypes) {
        setActivityTypes(result.activityTypes);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los tipos de actividad.",
        });
      }
    } catch (error: any) {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los tipos de actividad.",
      });
    } finally {
      setLoadingActivityTypes(false);
    }
  }, [crmConnection, getActivityTypes]);

  const handleEdit = async (id: string) => {
    if (pipelines.length === 0) {
      await handleLoadPipelines();
    }
    setEditingId(id);

    if (crmConnection?.provider) {
      const item =
        merchantType === "development"
          ? projectsWithConfig.find((p) => p.id === id)
          : rentalsTypesWithConfig.find((r) => r.id === id);
      const pipelineId = item?.crmConfiguration?.pipelineId || selectedPipelines[id];
      if (pipelineId && !stages[id]) {
        await handleLoadStages(id, pipelineId);
      }
      if (activityTypes.length === 0) {
        await handleLoadActivityTypes();
      }
    }
  };

  const handlePipelineChange = (id: string, pipelineId: number) => {
    setSelectedPipelines((prev) => ({ ...prev, [id]: pipelineId }));
    setStages((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleAddEventType = (
    id: string,
    eventType: LeadEventType,
    condition?: PipedriveCondition
  ) => {
    const currentMappings = eventMappings[id] || [];
    const alreadyExists = currentMappings.some(
      (m) => m.event === eventType && m.condition?.type === condition
    );
    if (alreadyExists) {
      return;
    }
    const newMapping: PipedriveEventMapping = { event: eventType };
    if (condition) {
      newMapping.condition = { type: condition };
    }
    setEventMappings((prev) => ({
      ...prev,
      [id]: [...currentMappings, newMapping],
    }));
  };

  const handleRemoveEventType = (
    id: string,
    eventType: LeadEventType,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => ({
      ...prev,
      [id]: (prev[id] || []).filter(
        (m) => !(m.event === eventType && m.condition?.type === condition)
      ),
    }));
  };

  const handleAddAction = (
    id: string,
    eventType: LeadEventType,
    action: PipedriveAction,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const existingMapping = mappings.find(
        (m) => m.event === eventType && m.condition?.type === condition
      );

      if (!existingMapping) {
        return prev;
      }

      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition) {
          const updated = { ...m };

          if (action === "moveToStage") {
            updated.moveToStage = { stageId: 0 };
          } else if (action === "addActivity") {
            updated.addActivities = [
              {
                activityType: "",
                activitySubject: "",
                activityTypeAppointment: undefined,
                activityTypeVirtualAppointment: undefined,
                activityDueDateType: "appointmentDate",
              },
            ];
          } else if (action === "addNote") {
            updated.addNote = { content: "" };
          } else if (action === "updateCustomFields") {
            updated.updateCustomFields = { customFields: [] };
          } else if (action === "setOwner") {
            updated.setOwner = true;
          } else if (action === "setAsLost") {
            updated.setAsLost = { lostReason: "" };
          } else if (action === "setAsOpen") {
            updated.setAsOpen = true;
          } else if (action === "createDeal") {
            updated.createDeal = { dealTitle: "{{name}}", stageId: 0 };
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleRemoveAction = (
    id: string,
    eventType: LeadEventType,
    action: PipedriveAction,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition) {
          const updated = { ...m } as any;
          if (action === "moveToStage") {
            delete updated.moveToStage;
          } else if (action === "addActivity") {
            delete updated.addActivities;
          } else if (action === "addNote") {
            delete updated.addNote;
          } else if (action === "updateCustomFields") {
            delete updated.updateCustomFields;
          } else if (action === "setOwner") {
            delete updated.setOwner;
          } else if (action === "setAsLost") {
            delete updated.setAsLost;
          } else if (action === "setAsOpen") {
            delete updated.setAsOpen;
          } else if (action === "createDeal") {
            delete updated.createDeal;
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleUpdateActionField = (
    id: string,
    eventType: LeadEventType,
    action: PipedriveAction,
    field: string,
    value: any,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition) {
          const updated = { ...m } as any;
          if (action === "moveToStage" && updated.moveToStage) {
            updated.moveToStage = { ...updated.moveToStage, [field]: value };
          } else if (action === "addNote" && updated.addNote) {
            updated.addNote = { ...updated.addNote, [field]: value };
          } else if (action === "updateCustomFields" && updated.updateCustomFields) {
            updated.updateCustomFields = { ...updated.updateCustomFields, [field]: value };
          } else if (action === "setAsLost" && updated.setAsLost) {
            updated.setAsLost = { ...updated.setAsLost, [field]: value };
          } else if (action === "createDeal" && updated.createDeal) {
            updated.createDeal = { ...updated.createDeal, [field]: value };
          }
          return updated;
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleAddCustomField = (
    id: string,
    eventType: LeadEventType,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.updateCustomFields) {
          return {
            ...m,
            updateCustomFields: {
              ...m.updateCustomFields,
              customFields: [
                ...(m.updateCustomFields.customFields || []),
                { fieldId: "", value: "" },
              ],
            },
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleRemoveCustomField = (
    id: string,
    eventType: LeadEventType,
    index: number,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.updateCustomFields) {
          return {
            ...m,
            updateCustomFields: {
              ...m.updateCustomFields,
              customFields: m.updateCustomFields.customFields.filter((_, i) => i !== index),
            },
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleUpdateCustomField = (
    id: string,
    eventType: LeadEventType,
    index: number,
    field: "fieldId" | "value",
    value: string,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.updateCustomFields) {
          const customFields = [...m.updateCustomFields.customFields];
          customFields[index] = { ...customFields[index], [field]: value };
          return {
            ...m,
            updateCustomFields: {
              ...m.updateCustomFields,
              customFields,
            },
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleAddActivity = (
    id: string,
    eventType: LeadEventType,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.addActivities) {
          return {
            ...m,
            addActivities: [
              ...m.addActivities,
              {
                activityType: "",
                activitySubject: "",
                activityTypeAppointment: undefined,
                activityTypeVirtualAppointment: undefined,
                activityDueDateType: "appointmentDate" as const,
              },
            ],
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleRemoveActivity = (
    id: string,
    eventType: LeadEventType,
    activityIndex: number,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.addActivities) {
          const newActivities = m.addActivities.filter((_, i) => i !== activityIndex);
          if (newActivities.length === 0) {
            const { addActivities, ...rest } = m;
            return rest;
          }
          return {
            ...m,
            addActivities: newActivities,
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleUpdateActivity = (
    id: string,
    eventType: LeadEventType,
    activityIndex: number,
    field: string,
    value: string,
    condition?: PipedriveCondition
  ) => {
    setEventMappings((prev) => {
      const mappings = prev[id] || [];
      const updatedMappings = mappings.map((m) => {
        if (m.event === eventType && m.condition?.type === condition && m.addActivities) {
          const activities = [...m.addActivities];
          activities[activityIndex] = { ...activities[activityIndex], [field]: value };
          return {
            ...m,
            addActivities: activities,
          };
        }
        return m;
      });
      return {
        ...prev,
        [id]: updatedMappings,
      };
    });
  };

  const handleSaveConfiguration = async (item: ProjectWithConfig | RentalsTypeWithConfig) => {
    if (!merchantId || !crmConnection?.provider) return;

    const selectedPipelineId = selectedPipelines[item.id];
    if (!selectedPipelineId) {
      toast.error("Error", {
        description: "Por favor selecciona un pipeline.",
      });
      return;
    }

    const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);
    if (!selectedPipeline) return;

    const mappings = eventMappings[item.id] || [];

    // Validate all required fields
    for (const mapping of mappings) {
      const eventLabel = getEventTypeLabel(mapping.event);

      if (mapping.moveToStage) {
        if (!mapping.moveToStage.stageId || mapping.moveToStage.stageId === 0) {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Mover a Etapa" requiere seleccionar una etapa.`,
          });
          return;
        }
      }

      if (mapping.addActivities && mapping.addActivities.length > 0) {
        for (let i = 0; i < mapping.addActivities.length; i++) {
          const activity = mapping.addActivities[i];
          const activityNum = i + 1;
          if (mapping.event === "lead.appointment-created") {
            if (!activity.activityTypeAppointment || activity.activityTypeAppointment === "") {
              toast.error("Error", {
                description: `Para el evento "${eventLabel}", la actividad ${activityNum} requiere seleccionar un tipo de actividad para cita presencial.`,
              });
              return;
            }
            if (
              !activity.activityTypeVirtualAppointment ||
              activity.activityTypeVirtualAppointment === ""
            ) {
              toast.error("Error", {
                description: `Para el evento "${eventLabel}", la actividad ${activityNum} requiere seleccionar un tipo de actividad para cita virtual.`,
              });
              return;
            }
          } else {
            if (!activity.activityType || activity.activityType === "") {
              toast.error("Error", {
                description: `Para el evento "${eventLabel}", la actividad ${activityNum} requiere seleccionar un tipo de actividad.`,
              });
              return;
            }
          }
          if (!activity.activitySubject || activity.activitySubject.trim() === "") {
            toast.error("Error", {
              description: `Para el evento "${eventLabel}", la actividad ${activityNum} requiere ingresar un asunto.`,
            });
            return;
          }
        }
      }

      if (mapping.addNote) {
        if (!mapping.addNote.content || mapping.addNote.content.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Agregar Nota" requiere ingresar un contenido.`,
          });
          return;
        }
      }

      if (mapping.updateCustomFields) {
        if (
          !mapping.updateCustomFields.customFields ||
          mapping.updateCustomFields.customFields.length === 0
        ) {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Actualizar Campos Personalizados" requiere al menos un campo.`,
          });
          return;
        }
        for (let i = 0; i < mapping.updateCustomFields.customFields.length; i++) {
          const field = mapping.updateCustomFields.customFields[i];
          if (!field.fieldId || field.fieldId.trim() === "") {
            toast.error("Error", {
              description: `Para el evento "${eventLabel}", el campo personalizado #${
                i + 1
              } requiere un ID de campo.`,
            });
            return;
          }
          if (!field.value || field.value.trim() === "") {
            toast.error("Error", {
              description: `Para el evento "${eventLabel}", el campo personalizado #${
                i + 1
              } requiere un valor.`,
            });
            return;
          }
        }
      }

      if (mapping.setAsLost) {
        if (!mapping.setAsLost.lostReason || mapping.setAsLost.lostReason.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Marcar como Perdido" requiere una razón de pérdida.`,
          });
          return;
        }
      }

      if (mapping.createDeal) {
        if (!mapping.createDeal.dealTitle || mapping.createDeal.dealTitle.trim() === "") {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Crear Deal" requiere un título.`,
            duration: 5000,
          });
          return;
        }
        if (!mapping.createDeal.stageId || mapping.createDeal.stageId === 0) {
          toast.error("Error", {
            description: `Para el evento "${eventLabel}", la acción "Crear Deal" requiere seleccionar una etapa inicial.`,
            duration: 5000,
          });
          return;
        }
      }
    }

    const pipedriveMappings: PipedriveEventMapping[] = mappings.map((mapping) => {
      let updated = { ...mapping };
      if (updated.moveToStage && updated.moveToStage.stageId && !updated.moveToStage.stageName) {
        const stage = stages[item.id]?.find((s) => s.id === updated.moveToStage!.stageId);
        if (stage) {
          updated = {
            ...updated,
            moveToStage: { ...updated.moveToStage, stageName: stage.name },
          };
        }
      }
      if (updated.createDeal && updated.createDeal.stageId && !updated.createDeal.stageName) {
        const stage = stages[item.id]?.find((s) => s.id === updated.createDeal!.stageId);
        if (stage) {
          updated = {
            ...updated,
            createDeal: { ...updated.createDeal, stageName: stage.name },
          };
        }
      }
      return updated;
    });

    if (item.crmConfiguration) {
      await updateConfiguration.mutateAsync({
        merchantId,
        id: item.crmConfiguration.id,
        pipedrive: {
          pipelineId: selectedPipeline.id,
          pipelineName: selectedPipeline.name,
        },
        pipedriveEventsMapping: pipedriveMappings,
      });
    } else {
      await createConfiguration.mutateAsync({
        merchantId,
        ...(merchantType === "development" && { projectId: item.id }),
        ...(merchantType === "rentals" && { rentalsType: item.id as RentalsType }),
        provider: crmConnection.provider,
        enabled: true,
        pipedrive: {
          pipelineId: selectedPipeline.id,
          pipelineName: selectedPipeline.name,
        },
        pipedriveEventsMapping: pipedriveMappings,
      });
    }
    setEditingId(null);
  };

  type AvailableEventOption = {
    event: LeadEventType;
    condition?: PipedriveCondition;
    label: string;
  };

  const getAvailableEventOptions = (id: string): AvailableEventOption[] => {
    const currentMappings = eventMappings[id] || [];
    const options: AvailableEventOption[] = [];

    for (const eventType of LEAD_EVENT_TYPES) {
      // For lead.inactivated, offer condition-based options
      if (eventType === "lead.inactivated") {
        // Check if we have a no-condition mapping
        const hasNoCondition = currentMappings.some((m) => m.event === eventType && !m.condition);
        if (!hasNoCondition) {
          options.push({
            event: eventType,
            condition: undefined,
            label: `${getEventTypeLabel(eventType)} (Sin condición)`,
          });
        }
        // Check each condition
        for (const condition of PIPEDRIVE_CONDITIONS) {
          const hasCondition = currentMappings.some(
            (m) => m.event === eventType && m.condition?.type === condition
          );
          if (!hasCondition) {
            options.push({
              event: eventType,
              condition,
              label: `${getEventTypeLabel(eventType)} - ${getPipedriveConditionLabel(condition)}`,
            });
          }
        }
      } else {
        // For other events, just check if it's already used (no conditions)
        const isUsed = currentMappings.some((m) => m.event === eventType);
        if (!isUsed) {
          options.push({
            event: eventType,
            condition: undefined,
            label: getEventTypeLabel(eventType),
          });
        }
      }
    }

    return options;
  };

  const getMappingsForItem = (id: string): PipedriveEventMapping[] => {
    return eventMappings[id] || [];
  };

  const getAvailableActions = (eventType: LeadEventType): PipedriveAction[] => {
    return getAvailableActionsForEvent(eventType);
  };

  if (isLoadingConfigurations || (merchantType === "development" && isLoadingProjects)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Spinner size="sm" />
          <p>
            {merchantType === "development" ? "Cargando proyectos..." : "Cargando configuración..."}
          </p>
        </div>
      </div>
    );
  }

  const itemsToRender =
    merchantType === "development" ? projectsWithConfig : rentalsTypesWithConfig;

  if (merchantType === "development" && (!projects || projects.length === 0)) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">No hay proyectos disponibles.</p>
      </div>
    );
  }

  if (merchantType === "rentals" && rentalsTypesWithConfig.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <p className="text-gray-600">No hay tipos de rentals disponibles.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-lg font-bold">
            {merchantType === "development"
              ? "Configuración por Proyecto"
              : "Configuración por Tipo de Negocio"}
          </p>
          <p className="text-sm text-gray-600">
            {merchantType === "development"
              ? "Asigna un pipeline a cada proyecto y configura los eventos"
              : "Asigna un pipeline a cada tipo de negocio (Arriendo residencial, Arriendo comercial, Venta) y configura los eventos"}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {itemsToRender.map((item) => {
            const isEditing = editingId === item.id;
            const hasPipeline = !!selectedPipelines[item.id] || !!item.crmConfiguration?.pipelineId;
            const mappings = getMappingsForItem(item.id);
            const availableEventOptions = getAvailableEventOptions(item.id);

            return (
              <div key={item.id} className="rounded-md border border-gray-200 p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">{item.name}</p>
                    <div className="flex items-center gap-2">
                      {item.crmConfiguration ? (
                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">
                          Configurado
                        </Badge>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">
                          Sin configurar
                        </Badge>
                      )}
                      {!isEditing && (
                        <Button
                          size="sm"
                          onClick={() => handleEdit(item.id)}
                          disabled={loadingPipelines && pipelines.length === 0}
                        >
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>

                  {isEditing ? (
                    loadingPipelines && pipelines.length === 0 ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <p className="text-xs text-gray-500">Cargando pipelines...</p>
                      </div>
                    ) : pipelines.length > 0 ? (
                      <>
                        <div className="flex flex-col gap-2">
                          <Label className="text-xs text-gray-600">Pipeline</Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={selectedPipelines[item.id] || ""}
                            onChange={(e) => {
                              const pipelineId = parseInt(e.target.value);
                              if (pipelineId) {
                                handlePipelineChange(item.id, pipelineId);
                                handleLoadStages(item.id, pipelineId);
                              }
                            }}
                          >
                            <option value="">Selecciona un pipeline</option>
                            {pipelines.map((pipeline) => (
                              <option key={pipeline.id} value={pipeline.id}>
                                {pipeline.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {item.crmConfiguration?.pipelineName && (
                          <p className="text-xs text-gray-500">
                            Pipeline actual: {item.crmConfiguration.pipelineName}
                          </p>
                        )}

                        {hasPipeline && (
                          <div className="mt-4">
                            <p className="mb-3 text-sm font-semibold">Mapeo de Eventos</p>
                            <div className="flex flex-col gap-4">
                              <div>
                                <p className="mb-2 text-xs font-semibold text-gray-600">
                                  Tipos de Eventos
                                </p>
                                {availableEventOptions.length > 0 ? (
                                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                                    {availableEventOptions.map((option) => (
                                      <div
                                        key={`${option.event}-${option.condition || "none"}`}
                                        className="rounded-md border border-gray-200 bg-gray-50 p-2"
                                      >
                                        <div className="flex items-center justify-between">
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="flex-1 cursor-help text-xs">
                                                  {option.label}
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                {getEventTypeDescription(option.event)}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="size-6"
                                            aria-label="Agregar evento"
                                            onClick={() =>
                                              handleAddEventType(
                                                item.id,
                                                option.event,
                                                option.condition
                                              )
                                            }
                                          >
                                            <Plus className="size-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs italic text-gray-500">
                                    Todos los tipos de eventos han sido agregados
                                  </p>
                                )}
                              </div>

                              {mappings.length > 0 && (
                                <div>
                                  <p className="mb-2 text-xs font-semibold text-gray-600">
                                    Acciones de Pipedrive
                                  </p>
                                  <div className="flex flex-col gap-3">
                                    {mappings.map((mapping) => {
                                      const mappingKey = `${mapping.event}-${
                                        mapping.condition?.type || "none"
                                      }`;
                                      const displayLabel = mapping.condition
                                        ? `${getEventTypeLabel(
                                            mapping.event
                                          )} - ${getPipedriveConditionLabel(
                                            mapping.condition.type
                                          )}`
                                        : getEventTypeLabel(mapping.event);

                                      return (
                                        <div
                                          key={mappingKey}
                                          className="rounded-md border border-gray-200 bg-gray-50 p-3"
                                        >
                                          <div className="flex flex-col gap-3">
                                            <div className="flex items-center justify-between">
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <div className="flex flex-col items-start gap-0">
                                                      <span className="cursor-help text-xs font-semibold text-blue-600">
                                                        {displayLabel}
                                                      </span>
                                                      {mapping.condition && (
                                                        <Badge className="mt-1 bg-purple-100 text-purple-800 border-purple-200 text-[10px]">
                                                          Condición activa
                                                        </Badge>
                                                      )}
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    {getEventTypeDescription(mapping.event)}
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="size-6 text-red-600 hover:text-red-700"
                                                aria-label="Eliminar evento"
                                                onClick={() =>
                                                  handleRemoveEventType(
                                                    item.id,
                                                    mapping.event,
                                                    mapping.condition?.type
                                                  )
                                                }
                                              >
                                                <Trash2 className="size-4" />
                                              </Button>
                                            </div>
                                            <Separator />
                                            <div className="flex flex-col gap-2">
                                              {getAvailableActions(mapping.event).map((action) => {
                                                const hasAction = !!(
                                                  (action === "moveToStage" &&
                                                    mapping.moveToStage) ||
                                                  (action === "addActivity" &&
                                                    mapping.addActivities &&
                                                    mapping.addActivities.length > 0) ||
                                                  (action === "addNote" && mapping.addNote) ||
                                                  (action === "updateCustomFields" &&
                                                    mapping.updateCustomFields) ||
                                                  (action === "setOwner" &&
                                                    mapping.setOwner === true) ||
                                                  (action === "setAsLost" && mapping.setAsLost) ||
                                                  (action === "setAsOpen" && mapping.setAsOpen) ||
                                                  (action === "createDeal" && mapping.createDeal)
                                                );
                                                return (
                                                  <div key={action}>
                                                    <div
                                                      className={`flex items-center gap-2 ${
                                                        hasAction ? "mb-2" : ""
                                                      }`}
                                                    >
                                                      <Checkbox
                                                        checked={hasAction}
                                                        onCheckedChange={(checked) => {
                                                          if (checked) {
                                                            handleAddAction(
                                                              item.id,
                                                              mapping.event,
                                                              action,
                                                              mapping.condition?.type
                                                            );
                                                          } else {
                                                            handleRemoveAction(
                                                              item.id,
                                                              mapping.event,
                                                              action,
                                                              mapping.condition?.type
                                                            );
                                                          }
                                                        }}
                                                      />
                                                      <label className="text-xs">
                                                        {getPipedriveActionLabel(action)}
                                                      </label>
                                                    </div>
                                                    {hasAction && (
                                                      <div className="ml-6 mt-2">
                                                        {action === "moveToStage" &&
                                                          mapping.moveToStage && (
                                                            <div className="flex flex-col gap-2">
                                                              <Label className="text-xs">
                                                                Etapa{" "}
                                                                <span className="text-red-500">
                                                                  *
                                                                </span>
                                                              </Label>
                                                              {loadingStages[item.id] ? (
                                                                <div className="flex items-center gap-2">
                                                                  <Spinner size="sm" />
                                                                  <p className="text-xs text-gray-500">
                                                                    Cargando etapas...
                                                                  </p>
                                                                </div>
                                                              ) : stages[item.id] &&
                                                                stages[item.id].length > 0 ? (
                                                                <select
                                                                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                  value={String(
                                                                    mapping.moveToStage.stageId ||
                                                                      ""
                                                                  )}
                                                                  onChange={(e) => {
                                                                    const selectedStageId =
                                                                      parseInt(e.target.value);
                                                                    const selectedStage = stages[
                                                                      item.id
                                                                    ]?.find(
                                                                      (s) =>
                                                                        s.id === selectedStageId
                                                                    );
                                                                    if (selectedStage) {
                                                                      handleUpdateActionField(
                                                                        item.id,
                                                                        mapping.event,
                                                                        action,
                                                                        "stageId",
                                                                        selectedStage.id,
                                                                        mapping.condition?.type
                                                                      );
                                                                      handleUpdateActionField(
                                                                        item.id,
                                                                        mapping.event,
                                                                        action,
                                                                        "stageName",
                                                                        selectedStage.name,
                                                                        mapping.condition?.type
                                                                      );
                                                                    }
                                                                  }}
                                                                  required
                                                                >
                                                                  <option value="">
                                                                    Selecciona una etapa
                                                                  </option>
                                                                  {stages[item.id].map((stage) => (
                                                                    <option
                                                                      key={stage.id}
                                                                      value={String(stage.id)}
                                                                    >
                                                                      {stage.name}
                                                                    </option>
                                                                  ))}
                                                                </select>
                                                              ) : selectedPipelines[item.id] ? (
                                                                <p className="text-xs text-gray-500">
                                                                  Las etapas se cargarán
                                                                  automáticamente al seleccionar un
                                                                  pipeline
                                                                </p>
                                                              ) : (
                                                                <p className="text-xs text-gray-500">
                                                                  Selecciona un pipeline primero
                                                                </p>
                                                              )}
                                                            </div>
                                                          )}
                                                        {action === "addActivity" &&
                                                          mapping.addActivities &&
                                                          mapping.addActivities.length > 0 && (
                                                            <div className="flex flex-col gap-3">
                                                              {mapping.addActivities.map(
                                                                (activity, activityIndex) => (
                                                                  <div
                                                                    key={activityIndex}
                                                                    className="rounded-md border border-gray-200 bg-gray-50 p-3"
                                                                  >
                                                                    <div className="mb-2 flex items-center justify-between">
                                                                      <p className="text-xs font-medium">
                                                                        Actividad{" "}
                                                                        {activityIndex + 1}
                                                                      </p>
                                                                      <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="size-6 text-red-600 hover:text-red-700"
                                                                        aria-label="Eliminar actividad"
                                                                        onClick={() =>
                                                                          handleRemoveActivity(
                                                                            item.id,
                                                                            mapping.event,
                                                                            activityIndex,
                                                                            mapping.condition?.type
                                                                          )
                                                                        }
                                                                      >
                                                                        <Trash2 className="size-4" />
                                                                      </Button>
                                                                    </div>
                                                                    <div className="flex flex-col gap-2">
                                                                      {mapping.event ===
                                                                      "lead.appointment-created" ? (
                                                                        <>
                                                                          <div className="flex flex-col gap-2">
                                                                            <Label className="text-xs">
                                                                              Tipo de Actividad
                                                                              (Cita Presencial){" "}
                                                                              <span className="text-red-500">
                                                                                *
                                                                              </span>
                                                                            </Label>
                                                                            {loadingActivityTypes ? (
                                                                              <Spinner size="sm" />
                                                                            ) : activityTypes.length >
                                                                              0 ? (
                                                                              <select
                                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                                value={
                                                                                  activity.activityTypeAppointment ||
                                                                                  ""
                                                                                }
                                                                                onChange={(e) => {
                                                                                  handleUpdateActivity(
                                                                                    item.id,
                                                                                    mapping.event,
                                                                                    activityIndex,
                                                                                    "activityTypeAppointment",
                                                                                    e.target.value,
                                                                                    mapping
                                                                                      .condition
                                                                                      ?.type
                                                                                  );
                                                                                  handleUpdateActivity(
                                                                                    item.id,
                                                                                    mapping.event,
                                                                                    activityIndex,
                                                                                    "activityType",
                                                                                    e.target.value,
                                                                                    mapping
                                                                                      .condition
                                                                                      ?.type
                                                                                  );
                                                                                }}
                                                                              >
                                                                                <option value="">
                                                                                  Selecciona tipo
                                                                                </option>
                                                                                {activityTypes.map(
                                                                                  (at) => (
                                                                                    <option
                                                                                      key={at.id}
                                                                                      value={
                                                                                        at.key_string
                                                                                      }
                                                                                    >
                                                                                      {at.name}
                                                                                    </option>
                                                                                  )
                                                                                )}
                                                                              </select>
                                                                            ) : (
                                                                              <p className="text-xs text-gray-500">
                                                                                Cargando...
                                                                              </p>
                                                                            )}
                                                                          </div>
                                                                          <div className="flex flex-col gap-2">
                                                                            <Label className="text-xs">
                                                                              Tipo de Actividad
                                                                              (Cita Virtual){" "}
                                                                              <span className="text-red-500">
                                                                                *
                                                                              </span>
                                                                            </Label>
                                                                            {loadingActivityTypes ? (
                                                                              <Spinner size="sm" />
                                                                            ) : activityTypes.length >
                                                                              0 ? (
                                                                              <select
                                                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                                value={
                                                                                  activity.activityTypeVirtualAppointment ||
                                                                                  ""
                                                                                }
                                                                                onChange={(e) =>
                                                                                  handleUpdateActivity(
                                                                                    item.id,
                                                                                    mapping.event,
                                                                                    activityIndex,
                                                                                    "activityTypeVirtualAppointment",
                                                                                    e.target.value,
                                                                                    mapping
                                                                                      .condition
                                                                                      ?.type
                                                                                  )
                                                                                }
                                                                              >
                                                                                <option value="">
                                                                                  Selecciona tipo
                                                                                </option>
                                                                                {activityTypes.map(
                                                                                  (at) => (
                                                                                    <option
                                                                                      key={at.id}
                                                                                      value={
                                                                                        at.key_string
                                                                                      }
                                                                                    >
                                                                                      {at.name}
                                                                                    </option>
                                                                                  )
                                                                                )}
                                                                              </select>
                                                                            ) : (
                                                                              <p className="text-xs text-gray-500">
                                                                                Cargando...
                                                                              </p>
                                                                            )}
                                                                          </div>
                                                                          <div className="flex flex-col gap-2">
                                                                            <Label className="text-xs">
                                                                              Fecha de Vencimiento{" "}
                                                                              <span className="text-red-500">
                                                                                *
                                                                              </span>
                                                                            </Label>
                                                                            <select
                                                                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                              value={
                                                                                activity.activityDueDateType ||
                                                                                "appointmentDate"
                                                                              }
                                                                              onChange={(e) =>
                                                                                handleUpdateActivity(
                                                                                  item.id,
                                                                                  mapping.event,
                                                                                  activityIndex,
                                                                                  "activityDueDateType",
                                                                                  e.target.value,
                                                                                  mapping.condition
                                                                                    ?.type
                                                                                )
                                                                              }
                                                                            >
                                                                              <option value="appointmentDate">
                                                                                Fecha de la cita
                                                                              </option>
                                                                              <option value="today">
                                                                                Hoy (fecha actual)
                                                                              </option>
                                                                            </select>
                                                                          </div>
                                                                        </>
                                                                      ) : (
                                                                        <div className="flex flex-col gap-2">
                                                                          <Label className="text-xs">
                                                                            Tipo de Actividad{" "}
                                                                            <span className="text-red-500">
                                                                              *
                                                                            </span>
                                                                          </Label>
                                                                          {loadingActivityTypes ? (
                                                                            <Spinner size="sm" />
                                                                          ) : activityTypes.length >
                                                                            0 ? (
                                                                            <select
                                                                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                              value={
                                                                                activity.activityType ||
                                                                                ""
                                                                              }
                                                                              onChange={(e) =>
                                                                                handleUpdateActivity(
                                                                                  item.id,
                                                                                  mapping.event,
                                                                                  activityIndex,
                                                                                  "activityType",
                                                                                  e.target.value,
                                                                                  mapping.condition
                                                                                    ?.type
                                                                                )
                                                                              }
                                                                            >
                                                                              <option value="">
                                                                                Selecciona tipo
                                                                              </option>
                                                                              {activityTypes.map(
                                                                                (at) => (
                                                                                  <option
                                                                                    key={at.id}
                                                                                    value={
                                                                                      at.key_string
                                                                                    }
                                                                                  >
                                                                                    {at.name}
                                                                                  </option>
                                                                                )
                                                                              )}
                                                                            </select>
                                                                          ) : (
                                                                            <p className="text-xs text-gray-500">
                                                                              Cargando...
                                                                            </p>
                                                                          )}
                                                                        </div>
                                                                      )}
                                                                      <div className="flex flex-col gap-2">
                                                                        <Label className="text-xs">
                                                                          Asunto{" "}
                                                                          <span className="text-red-500">
                                                                            *
                                                                          </span>
                                                                        </Label>
                                                                        <Input
                                                                          className="bg-white text-sm"
                                                                          value={
                                                                            activity.activitySubject ||
                                                                            ""
                                                                          }
                                                                          onChange={(e) =>
                                                                            handleUpdateActivity(
                                                                              item.id,
                                                                              mapping.event,
                                                                              activityIndex,
                                                                              "activitySubject",
                                                                              e.target.value,
                                                                              mapping.condition
                                                                                ?.type
                                                                            )
                                                                          }
                                                                          required
                                                                        />
                                                                        <div className="mt-1 flex items-center">
                                                                          <VariableHelpTooltip />
                                                                        </div>
                                                                      </div>
                                                                    </div>
                                                                  </div>
                                                                )
                                                              )}
                                                              <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() =>
                                                                  handleAddActivity(
                                                                    item.id,
                                                                    mapping.event,
                                                                    mapping.condition?.type
                                                                  )
                                                                }
                                                              >
                                                                <Plus className="mr-2 size-4" />
                                                                Agregar otra actividad
                                                              </Button>
                                                            </div>
                                                          )}
                                                        {action === "addNote" &&
                                                          mapping.addNote && (
                                                            <div className="flex flex-col gap-2">
                                                              <Label className="text-xs">
                                                                Contenido{" "}
                                                                <span className="text-red-500">
                                                                  *
                                                                </span>
                                                              </Label>
                                                              <Input
                                                                className="text-sm"
                                                                value={
                                                                  mapping.addNote.content || ""
                                                                }
                                                                onChange={(e) =>
                                                                  handleUpdateActionField(
                                                                    item.id,
                                                                    mapping.event,
                                                                    action,
                                                                    "content",
                                                                    e.target.value,
                                                                    mapping.condition?.type
                                                                  )
                                                                }
                                                                required
                                                              />
                                                              <div className="mt-1 flex items-center">
                                                                <VariableHelpTooltip />
                                                              </div>
                                                            </div>
                                                          )}
                                                        {action === "updateCustomFields" &&
                                                          mapping.updateCustomFields && (
                                                            <div className="flex flex-col gap-2">
                                                              {mapping.updateCustomFields.customFields.map(
                                                                (field, index) => (
                                                                  <div
                                                                    key={index}
                                                                    className="flex items-center gap-2"
                                                                  >
                                                                    <Input
                                                                      className="text-sm"
                                                                      placeholder="ID Campo"
                                                                      value={field.fieldId}
                                                                      onChange={(e) =>
                                                                        handleUpdateCustomField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          index,
                                                                          "fieldId",
                                                                          e.target.value,
                                                                          mapping.condition?.type
                                                                        )
                                                                      }
                                                                      required
                                                                    />
                                                                    <Input
                                                                      className="text-sm"
                                                                      placeholder="Valor"
                                                                      value={field.value}
                                                                      onChange={(e) =>
                                                                        handleUpdateCustomField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          index,
                                                                          "value",
                                                                          e.target.value,
                                                                          mapping.condition?.type
                                                                        )
                                                                      }
                                                                      required
                                                                    />
                                                                    <Button
                                                                      variant="ghost"
                                                                      size="icon"
                                                                      className="size-6 text-red-600 hover:text-red-700"
                                                                      aria-label="Eliminar campo"
                                                                      onClick={() =>
                                                                        handleRemoveCustomField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          index,
                                                                          mapping.condition?.type
                                                                        )
                                                                      }
                                                                    >
                                                                      <Trash2 className="size-4" />
                                                                    </Button>
                                                                  </div>
                                                                )
                                                              )}
                                                              <Button
                                                                size="sm"
                                                                onClick={() =>
                                                                  handleAddCustomField(
                                                                    item.id,
                                                                    mapping.event,
                                                                    mapping.condition?.type
                                                                  )
                                                                }
                                                              >
                                                                <Plus className="mr-2 size-4" />
                                                                Agregar Campo
                                                              </Button>
                                                            </div>
                                                          )}
                                                        {action === "setOwner" &&
                                                          mapping.setOwner && (
                                                            <p className="text-xs text-gray-600">
                                                              El propietario se asignará
                                                              automáticamente
                                                            </p>
                                                          )}
                                                        {action === "setAsLost" &&
                                                          mapping.setAsLost && (
                                                            <div className="flex flex-col gap-2">
                                                              <Label className="text-xs">
                                                                Razón de Pérdida{" "}
                                                                <span className="text-red-500">
                                                                  *
                                                                </span>
                                                              </Label>
                                                              <Input
                                                                className="text-sm"
                                                                value={
                                                                  mapping.setAsLost.lostReason || ""
                                                                }
                                                                onChange={(e) =>
                                                                  handleUpdateActionField(
                                                                    item.id,
                                                                    mapping.event,
                                                                    action,
                                                                    "lostReason",
                                                                    e.target.value,
                                                                    mapping.condition?.type
                                                                  )
                                                                }
                                                                required
                                                              />
                                                            </div>
                                                          )}
                                                        {action === "setAsOpen" &&
                                                          mapping.setAsOpen && (
                                                            <p className="text-xs text-gray-600">
                                                              El deal se marcará como abierto
                                                              automáticamente
                                                            </p>
                                                          )}
                                                        {action === "createDeal" &&
                                                          mapping.createDeal && (
                                                            <div className="flex flex-col gap-2">
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Título del Deal{" "}
                                                                  <span className="text-red-500">
                                                                    *
                                                                  </span>
                                                                </Label>
                                                                <Input
                                                                  value={
                                                                    mapping.createDeal.dealTitle
                                                                  }
                                                                  onChange={(e) =>
                                                                    handleUpdateActionField(
                                                                      item.id,
                                                                      mapping.event,
                                                                      action,
                                                                      "dealTitle",
                                                                      e.target.value,
                                                                      mapping.condition?.type
                                                                    )
                                                                  }
                                                                  placeholder="{{name}}"
                                                                  className="bg-gray-50"
                                                                />
                                                                <p className="text-xs text-gray-500">
                                                                  Usa {"{{name}}"} para incluir el
                                                                  nombre del lead
                                                                </p>
                                                              </div>
                                                              <div className="flex flex-col gap-2">
                                                                <Label className="text-xs">
                                                                  Etapa inicial{" "}
                                                                  <span className="text-red-500">
                                                                    *
                                                                  </span>
                                                                </Label>
                                                                {loadingStages[item.id] ? (
                                                                  <div className="flex items-center gap-2">
                                                                    <Spinner size="sm" />
                                                                    <p className="text-xs text-gray-500">
                                                                      Cargando etapas...
                                                                    </p>
                                                                  </div>
                                                                ) : stages[item.id] &&
                                                                  stages[item.id].length > 0 ? (
                                                                  <select
                                                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                                    value={String(
                                                                      mapping.createDeal.stageId ||
                                                                        ""
                                                                    )}
                                                                    onChange={(e) => {
                                                                      const selectedStageId =
                                                                        parseInt(e.target.value);
                                                                      const selectedStage = stages[
                                                                        item.id
                                                                      ]?.find(
                                                                        (s) =>
                                                                          s.id === selectedStageId
                                                                      );
                                                                      if (selectedStage) {
                                                                        handleUpdateActionField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          action,
                                                                          "stageId",
                                                                          selectedStage.id,
                                                                          mapping.condition?.type
                                                                        );
                                                                        handleUpdateActionField(
                                                                          item.id,
                                                                          mapping.event,
                                                                          action,
                                                                          "stageName",
                                                                          selectedStage.name,
                                                                          mapping.condition?.type
                                                                        );
                                                                      }
                                                                    }}
                                                                    required
                                                                  >
                                                                    <option value="">
                                                                      Selecciona una etapa
                                                                    </option>
                                                                    {stages[item.id].map(
                                                                      (stage) => (
                                                                        <option
                                                                          key={stage.id}
                                                                          value={String(stage.id)}
                                                                        >
                                                                          {stage.name}
                                                                        </option>
                                                                      )
                                                                    )}
                                                                  </select>
                                                                ) : selectedPipelines[item.id] ? (
                                                                  <p className="text-xs text-gray-500">
                                                                    Las etapas se cargarán
                                                                    automáticamente al seleccionar
                                                                    un pipeline
                                                                  </p>
                                                                ) : (
                                                                  <p className="text-xs text-gray-500">
                                                                    Selecciona un pipeline primero
                                                                  </p>
                                                                )}
                                                              </div>
                                                            </div>
                                                          )}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveConfiguration(item)}
                            disabled={
                              createConfiguration.isLoading ||
                              updateConfiguration.isLoading ||
                              !selectedPipelines[item.id]
                            }
                          >
                            {item.crmConfiguration ? "Actualizar" : "Guardar"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            Cancelar
                          </Button>
                        </div>
                      </>
                    ) : null
                  ) : (
                    <div className="flex flex-col gap-1">
                      <p className="text-xs text-gray-500">
                        {item.crmConfiguration
                          ? `Pipeline configurado: ${item.crmConfiguration.pipelineName}`
                          : "Haz clic en Editar para configurar el pipeline"}
                      </p>
                      {item.crmConfiguration?.pipedriveEventsMapping &&
                        item.crmConfiguration.pipedriveEventsMapping.length > 0 && (
                          <p className="text-xs text-gray-500">
                            Eventos:{" "}
                            {item.crmConfiguration.pipedriveEventsMapping
                              .map((m: any) =>
                                m.condition
                                  ? `${getEventTypeLabel(m.event)} (${getPipedriveConditionLabel(
                                      m.condition.type
                                    )})`
                                  : getEventTypeLabel(m.event)
                              )
                              .join(", ")}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PipedriveCrmConfiguration;
